import React, { useState, useEffect } from 'react';
import { OnboardingStepProps } from '../../../types/onboarding';
import { Bio } from '../../../components/Profile/types';
import { StepTitle, StepDescription, StepContent } from '../../../styles/onboarding/Onboarding.styles';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { LocationSelector } from '../../shared';

const InputGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.text.primary};
`;

const LocationButton = styled.button`
  margin-top: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LocationHint = styled.p`
  margin: ${theme.spacing.xs} 0 0 0;
  font-size: 0.85rem;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  background: ${theme.colors.background.card};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  background: ${theme.colors.background.card};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const BioTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  background: ${theme.colors.background.card};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const CharCount = styled.div<{ $isValid: boolean }>`
  margin-top: ${theme.spacing.sm};
  font-size: 0.9rem;
  color: ${props => props.$isValid ? theme.colors.text.secondary : theme.colors.error};
  text-align: right;
`;

const MIN_CHARACTERS = 50;
const MAX_CHARACTERS = 160;

export const ProfileStep: React.FC<OnboardingStepProps> = ({
    data,
    onChange
}) => {
    const [bioContent, setBioContent] = useState(data?.bio?.content || '');
    const [occupation, setOccupation] = useState(data?.occupation || '');
    const [education, setEducation] = useState(data?.education || '');
    const [location, setLocation] = useState({
        city: data?.location?.city || '',
        state: data?.location?.state || '',
        country: data?.location?.country || '',
        coordinates: data?.location?.coordinates,
    });
    // Exact coordinates, if the browser will give them. Everything that talks
    // about distance in this app reads latitude/longitude, and a typed city
    // name is not that — so this asks, and the backend geocodes the city when
    // the answer is no.
    const [locating, setLocating] = useState(false);
    const [locationNote, setLocationNote] = useState<string | null>(null);

    const useMyLocation = () => {
        if (!navigator.geolocation) {
            setLocationNote('This browser cannot share your location — your city will be used instead.');
            return;
        }
        setLocating(true);
        setLocationNote(null);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocation((prev) => ({
                    ...prev,
                    coordinates: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    },
                }));
                setLocating(false);
                setLocationNote('Using your exact location for distance.');
            },
            () => {
                // Denied, unavailable or timed out — all the same to us, and
                // none of them are worth an error. The city still works.
                setLocating(false);
                setLocationNote('No problem — we will use the city above for distance instead.');
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 },
        );
    };

    useEffect(() => {
        const characterCount = bioContent.length;
        // Bio is optional in onboarding, but if they enter it, let's keep the validation for quality
        const isValid = bioContent === '' || (characterCount >= MIN_CHARACTERS && characterCount <= MAX_CHARACTERS);

        onChange({
            bio: {
                content: bioContent,
                characterCount,
                maxCharacters: MAX_CHARACTERS,
                lastUpdated: new Date()
            },
            occupation,
            education,
            location
        }, isValid);
    }, [bioContent, occupation, education, location]);

    return (
        <>
            <StepTitle>Complete Your Profile</StepTitle>
            <StepDescription>
                Adding more details helps our AI find better matches for you.
            </StepDescription>
            <StepContent>
                <InputGroup>
                    <Label>Where do you live?</Label>
                    <LocationSelector
                        value={location}
                        onChange={(next) => setLocation((prev) => ({ ...next, coordinates: prev.coordinates }))}
                    />
                    <LocationButton type="button" onClick={useMyLocation} disabled={locating}>
                        {locating
                            ? 'Getting your location…'
                            : location.coordinates
                                ? '✓ Using your exact location'
                                : '📍 Use my exact location'}
                    </LocationButton>
                    {locationNote && <LocationHint>{locationNote}</LocationHint>}
                </InputGroup>

                <InputGroup>
                    <Label>What do you do for work?</Label>
                    <Input
                        placeholder="Occupation"
                        value={occupation}
                        onChange={(e) => setOccupation(e.target.value)}
                    />
                </InputGroup>

                <InputGroup>
                    <Label>What is your education level?</Label>
                    <Select value={education} onChange={(e) => setEducation(e.target.value)}>
                        <option value="">Select education</option>
                        <option value="High School">High School</option>
                        <option value="Associate">Associate's Degree</option>
                        <option value="Bachelor">Bachelor's Degree</option>
                        <option value="Master">Master's Degree</option>
                        <option value="PhD">PhD / Doctorate</option>
                        <option value="Other">Other</option>
                    </Select>
                </InputGroup>

                <InputGroup>
                    <Label>About You</Label>
                    <BioTextarea
                        value={bioContent}
                        onChange={(e) => setBioContent(e.target.value)}
                        placeholder="Tell us about yourself... What are your interests? What are you looking for? What makes you unique?"
                        maxLength={MAX_CHARACTERS}
                    />
                    <CharCount $isValid={bioContent === '' || (bioContent.length >= MIN_CHARACTERS && bioContent.length <= MAX_CHARACTERS)}>
                        {bioContent.length} / {MAX_CHARACTERS} characters
                        {bioContent.length > 0 && bioContent.length < MIN_CHARACTERS && ` (minimum ${MIN_CHARACTERS} for a quality profile)`}
                    </CharCount>
                </InputGroup>
            </StepContent>
        </>
    );
};
